import Box from '@mui/material/Box'
import { type EnrollmentData, NewEnrollmentStatus } from '../../../core/types'
import { Modal } from '../../Modal'
import { EnrollmentChip } from '../EnrollmentChip'
import { ProgramVerificationChip } from '../../Program/ProgramVerificationChip'
import { ProgramTracker } from '../ProgramTracker'
import { sendGet, sendPost } from '../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { useRefreshDispatch } from '../../../ContextProviders/RefreshProvider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type SelectOption, SelectWithLabel } from '../../SelectWithLabel'
import { TextAreaWithLabel } from '../../TextArea'
import { CareManagementSummary } from '../../CareManagement/CareManagementSummary'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import S3Image from '../../S3Image'
import { Alert, Paper } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

interface Props {
  open: boolean
  enrollmentData: EnrollmentData
  onClose: () => void
}

interface FormFields {
  denyReasonId?: number
  notes: string
  referToProviderId?: number
}

const DeclineEnrollmentModal = (props: Props): JSX.Element => {
  const { open, enrollmentData, onClose } = props
  const [denyReasons, setDenyReasons] = useState<Array<SelectOption<number>>>([])
  const [providers, setProviders] = useState<Array<SelectOption<number>>>([])
  const [nextProvider, setNextProvider] = useState<{ s3Key: string | null, name: string }>({ s3Key: null, name: '' })
  const [formFields, setFormFields] = useState<FormFields>({
    denyReasonId: undefined,
    notes: '',
    referToProviderId: undefined
  })
  const [hasPendingReleaseEnrollments, setHasPendingReleaseEnrollments] = useState<boolean | null>(null)
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: reasonResponse, success: reasonSuccess } = await sendGet('/ReferralSearch/GetReferralDenyReasons', {})
      const { response: pendingResponse, success: pendingSuccess } = await sendGet(`/NewEnrollment/HasPendingReleaseEnrollments?id=${enrollmentData.id}`, {})
      setDenyReasons(reasonSuccess && reasonResponse)
      setHasPendingReleaseEnrollments(pendingSuccess && pendingResponse)

      if (pendingResponse === true) {
        const { response: nextProviderResponse, success: nextProviderSuccess } = await sendGet(`/NewEnrollment/GetNextProvider?enrollmentId=${enrollmentData.id}`, {})
        setNextProvider(nextProviderSuccess && nextProviderResponse)
      } else if (pendingResponse === false) {
        const { response: providersResponse, success: providersSuccess } = await sendGet(`/NewEnrollment/GetProviders?isdid=${enrollmentData.isdId ?? ''}&serviceId=${enrollmentData.serviceId ?? ''}`, {})
        setProviders(providersSuccess && providersResponse.map((r: any) => ({
          name: r.serviceProviderName,
          id: r.id
        })))
      }
    }

    void fetchData()
  }, [])

  const handleDeclineEnrollment = async (): Promise<void> => {
    if (formFields.denyReasonId === undefined) {
      toast.error('All fields are required')
      return
    }

    const { response } = await sendPost(`/NewEnrollment/DeclineEnrollment?id=${enrollmentData.id}`, formFields)
    if (response.success === true) {
      toast.success(response.message)
      onClose()
      refresh()
    } else {
      response.errors.forEach((e: any) => toast.error(e.error))
    }
  }

  return <Modal
    onClose={onClose}
    open={open}
    onConfirm={handleDeclineEnrollment}
    title='Decline Enrollment'
    confirmButtonText='Decline Enrollment'
    cancelButtonText='Cancel'
    confirmationContent={
      <Stack direction='column' spacing={2}>
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{enrollmentData.providerName}</Typography>
        <Stack direction='row' spacing={1}>
          {enrollmentData.serviceS3Key != null ? <S3Image style={{ width: '30px', height: '30px' }} imageKey={enrollmentData.serviceS3Key} /> : <SchoolOutlinedIcon sx={{ width: '30px', height: '30px' }} />}
          <Typography>{enrollmentData.serviceName}</Typography>
          <ProgramVerificationChip programName={enrollmentData.programName} status={enrollmentData.programVerificationStatus} timeSpanName={enrollmentData.programTimeSpanName} showStatusName={true} />
        </Stack>
        {enrollmentData.sessionId !== null && <ProgramTracker programId={enrollmentData.isdServiceProviderProgramId} sessionId={enrollmentData.sessionId} />}
        <Box>
          <SelectWithLabel
            width='100%'
            labelContent={<Typography variant='subtitle2'>Reason</Typography>}
            value={formFields.denyReasonId}
            onChange={(e) => { setFormFields({ ...formFields, denyReasonId: Number(e.target.value) }) }}
            name={'denyReason'}
            options={denyReasons}
          />
        </Box>
        <Box>
          <TextAreaWithLabel
            showRequiredText
            label='Notes'
            toolTip='Any additional notes regarding the denial'
            value={formFields.notes}
            onChange={(e) => { setFormFields({ ...formFields, notes: e.target.value }) }}
            name={'notes'}
          />
        </Box>
        {hasPendingReleaseEnrollments === false && <Box>
          <SelectWithLabel
            includeNoneOption
            showRequiredText
            width='100%'
            labelContent={<Typography variant='subtitle2'>Refer to:</Typography>}
            value={formFields.referToProviderId}
            onChange={(e) => { setFormFields({ ...formFields, referToProviderId: Number(e.target.value) }) }}
            name={'referToProvider'}
            options={providers}
          />
        </Box>}
        {hasPendingReleaseEnrollments === true && <Stack direction='column' spacing={2}>
          <Alert
            severity='warning'
            iconMapping={{ warning: <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'black', display: 'flex', alignSelf: 'center' }} /> }}
            sx={{ border: '1px solid gold', borderRadius: '10px' }}
          >
            Currently, you cannot refer to a new provider. This child has a service that will automatically be released when you decline.
          </Alert>
          <Paper elevation={0} sx={{ border: '1px solid #514f5342', borderRadius: '10px', p: '20px' }}>
            <Typography gutterBottom variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
              Next Provider Details
            </Typography>
            <Stack direction='row' spacing={2}>
              {nextProvider.s3Key != null ? <S3Image style={{ width: '30px', height: '30px' }} imageKey={nextProvider.s3Key} /> : <SchoolOutlinedIcon sx={{ width: '30px', height: '30px' }} />}
              <Typography>{nextProvider.name}</Typography>
            </Stack>
          </Paper>
        </Stack>}
        <CareManagementSummary childID={enrollmentData.childId} childName={enrollmentData.childName} variant='card' />
        <Stack direction='row' spacing={2}>
          <Typography>New Enrollment Status</Typography>
          <EnrollmentChip statusId={NewEnrollmentStatus.Declined} />
        </Stack>
      </Stack>
    }
  />
}

export default DeclineEnrollmentModal
